<script setup>
// Renderiza a tela de config de um nó a partir do esquema declarativo (configSchema.js).
// Usado pelos nós simples/médios; nós complexos têm componente dedicado.
import { computed, inject, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import FlowField from './FlowField.vue';
import { SCHEMAS } from './configSchema';

const props = defineProps({ node: { type: Object, required: true } });

const { t } = useI18n();

// Traduz apenas strings que são chaves i18n (prefixo CHATBOT.);
// placeholders literais (ex.: "5", "Olá {{contato}}") são mantidos como estão.
const tr = s =>
  typeof s === 'string' && s.startsWith('CHATBOT.') ? t(s) : s || '';
const aux = inject('flowAux', {});
const d = computed(() => props.node.data);
const schema = computed(
  () => SCHEMAS[props.node.data.nodeType] || { fields: [] }
);

const visibleFields = computed(() =>
  schema.value.fields.filter(f => {
    if (!f.showIf) return true;
    const cur = d.value[f.showIf.key];
    if (f.showIf.truthy) return !!cur;
    return cur === f.showIf.value;
  })
);

// Options estáticas (f.options) têm label como chave i18n → traduz.
// Options dinâmicas (optionsFrom) vêm de dados da conta → não traduz.
const optionsFor = f => {
  if (!f.optionsFrom)
    return (f.options || []).map(o => ({ ...o, label: tr(o.label) }));
  const src = aux[f.optionsFrom] || [];
  if (f.optionsFrom === 'agents')
    return [
      { value: '', label: '—' },
      ...src.map(a => ({ value: a.id, label: `${a.name}` })),
    ];
  if (f.optionsFrom === 'teams')
    return [
      { value: '', label: '—' },
      ...src.map(tm => ({ value: tm.id, label: tm.name })),
    ];
  if (f.optionsFrom === 'labels')
    return [
      { value: '', label: '—' },
      ...src.map(l => ({ value: l.title, label: l.title })),
    ];
  if (f.optionsFrom === 'slaPolicies')
    return [
      { value: '', label: '—' },
      ...src.map(s => ({ value: s.id, label: s.name })),
    ];
  if (f.optionsFrom === 'knowledgeBases')
    return [
      { value: '', label: '—' },
      ...src.map(kb => ({
        value: kb.id,
        label: `${kb.name} (${kb.documents_count})`,
      })),
    ];
  return (f.options || []).map(o => ({ ...o, label: tr(o.label) }));
};

// get/set considerando arrayWrap (ex.: labels[0])
const model = f => ({
  get: () => (f.arrayWrap ? (d.value[f.arrayWrap] || [])[0] : d.value[f.key]),
  set: v => {
    if (f.arrayWrap) d.value[f.arrayWrap] = v ? [v] : [];
    else d.value[f.key] = v;
  },
});

onMounted(() => {
  // aplica defaults sem sobrescrever valores existentes
  schema.value.fields.forEach(f => {
    if (f.default !== undefined && d.value[f.key] === undefined)
      d.value[f.key] = f.default;
  });
});
</script>

<template>
  <div>
    <FlowField
      v-for="(f, i) in visibleFields"
      :key="f.key + i"
      :model-value="model(f).get()"
      :label="tr(f.label)"
      :type="f.type"
      :placeholder="tr(f.placeholder)"
      :options="optionsFor(f)"
      :rows="f.rows || 3"
      :hint="tr(f.hint)"
      :min="f.min"
      :max="f.max"
      :step="f.step"
      :picker="!!f.picker"
      @update:model-value="model(f).set($event)"
    />
    <p v-if="schema.note" class="text-xs text-n-slate-10 mt-1">
      {{ t(schema.note) }}
    </p>
    <p
      v-if="!visibleFields.length && !schema.note"
      class="text-xs text-n-slate-10 mt-1"
    >
      {{ t('CHATBOT.schema.noConfig') }}
    </p>
  </div>
</template>
